import React, { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { motion, AnimatePresence } from 'framer-motion';

// ── Game data ─────────────────────────────────────────────────────────────────

const PATCH_COMPONENTS = [
  {
    id: 'firewall',
    label: 'Firewall',
    sublabel: 'IT-OT Boundary',
    color: 'border-orange-400 bg-orange-900/40 text-orange-200',
    activeColor: 'bg-orange-500/20 border-orange-400',
  },
  {
    id: 'ids-ot',
    label: 'IDS Sensor',
    sublabel: 'OT Control Zone',
    color: 'border-sky-400 bg-sky-900/40 text-sky-200',
    activeColor: 'bg-sky-500/20 border-sky-400',
  },
  {
    id: 'ids-field',
    label: 'Protocol Filter',
    sublabel: 'Field Device Zone',
    color: 'border-teal-400 bg-teal-900/40 text-teal-200',
    activeColor: 'bg-teal-500/20 border-teal-400',
  },
  {
    id: 'segmentation',
    label: 'Segmentation',
    sublabel: 'OT / Field Boundary',
    color: 'border-violet-400 bg-violet-900/40 text-violet-200',
    activeColor: 'bg-violet-500/20 border-violet-400',
  },
];

// SVG layout: viewBox 0 0 820 280
// Nodes: External(70,140), Corporate(210,140), DMZ(360,140), OT(510,140), Field(660,140)
const NODES = [
  { id: 'external',  label: 'External\nInternet', cx: 70,  cy: 140 },
  { id: 'corporate', label: 'Corporate\nIT',       cx: 210, cy: 140 },
  { id: 'dmz',       label: 'DMZ',                cx: 360, cy: 140 },
  { id: 'ot',        label: 'OT Control\nZone',   cx: 510, cy: 140 },
  { id: 'field',     label: 'Field\nDevices',     cx: 660, cy: 140 },
];

const CONNECTIONS = [[0,1],[1,2],[2,3],[3,4]];

// Drop slots: each has a position on the SVG and which component id it accepts
const SLOTS = [
  {
    id: 'slot-firewall',
    label: 'IT-OT Boundary',
    cx: 435, cy: 90,
    accepts: 'firewall',
    connectionFrom: 2,  // DMZ → OT connection index in CONNECTIONS
    connectionTo: 3,
  },
  {
    id: 'slot-ids-ot',
    label: 'OT Control Zone',
    cx: 510, cy: 50,
    accepts: 'ids-ot',
  },
  {
    id: 'slot-ids-field',
    label: 'Field Device Zone',
    cx: 660, cy: 50,
    accepts: 'ids-field',
  },
  {
    id: 'slot-segmentation',
    label: 'OT / Field Boundary',
    cx: 585, cy: 200,
    accepts: 'segmentation',
    connectionFrom: 3,
    connectionTo: 4,
    radius: 28,
  },
];

const NODE_COLOR = '#374151';
const NODE_STROKE = '#6b7280';
const SLOT_RADIUS = 22;

// ── Sub-components ────────────────────────────────────────────────────────────

function DraggableCard({ component, isPlaced }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: component.id,
    disabled: isPlaced,
  });

  if (isPlaced) return null;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`
        border rounded-lg px-3 py-2 cursor-grab select-none
        transition-opacity touch-none
        ${component.color}
        ${isDragging ? 'opacity-30' : 'opacity-100'}
      `}
    >
      <div className="text-sm font-semibold leading-tight">{component.label}</div>
    </div>
  );
}

function CardPreview({ component }) {
  if (!component) return null;
  return (
    <div className={`border rounded-lg px-3 py-2 select-none shadow-xl ${component.color}`}>
      <div className="text-sm font-semibold leading-tight">{component.label}</div>
    </div>
  );
}

function DropSlot({ slot, placedComponentId, isFlashing }) {
  const { setNodeRef, isOver } = useDroppable({ id: slot.id });
  const r = slot.radius ?? SLOT_RADIUS;

  const placed = placedComponentId
    ? PATCH_COMPONENTS.find(c => c.id === placedComponentId)
    : null;

  return (
    <g>
      {/* connector line from slot to nearest node if placed */}
      {placed && (
        <>
          <line
            x1={slot.cx}
            y1={slot.cy}
            x2={NODES.find(n => n.id === 'ot')?.cx ?? slot.cx}
            y2={140}
            stroke="transparent"
          />
        </>
      )}

      {/* foreign object for the droppable div */}
      <foreignObject
        x={slot.cx - r}
        y={slot.cy - r}
        width={r * 2}
        height={r * 2}
      >
        <motion.div
          ref={setNodeRef}
          animate={isFlashing ? { backgroundColor: ['#7f1d1d', '#1f2937', '#7f1d1d', '#1f2937'] } : {}}
          transition={{ duration: 0.4, repeat: 1 }}
          className={`
            w-full h-full rounded-full border-2 flex items-center justify-center
            transition-colors duration-150
            ${placed
              ? 'border-green-400 bg-green-900/60'
              : isOver
                ? 'border-yellow-400 bg-yellow-900/40'
                : 'border-dashed border-slate-500 bg-slate-800/60'
            }
          `}
          title={slot.label}
        />
      </foreignObject>

      {/* placed tool name rendered as SVG text over the circle */}
      {placed && (() => {
        const words = placed.label.split(' ');
        const lineHeight = 9;
        const startDy = -((words.length - 1) * lineHeight) / 2;
        return (
          <text
            x={slot.cx}
            y={slot.cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={7}
            fill="#86efac"
            fontFamily="system-ui, sans-serif"
            fontWeight="bold"
            style={{ pointerEvents: 'none' }}
          >
            {words.map((word, i) => (
              <tspan key={i} x={slot.cx} dy={i === 0 ? startDy : lineHeight}>
                {word}
              </tspan>
            ))}
          </text>
        );
      })()}

      {/* label below slot */}
      <text
        x={slot.cx}
        y={slot.cy + r + 13}
        textAnchor="middle"
        fontSize={9}
        fill={placed ? '#86efac' : '#94a3b8'}
        fontFamily="system-ui, sans-serif"
      >
        {slot.label}
      </text>
    </g>
  );
}

// Animates a connection line drawing in (stroke-dashoffset technique)
function AnimatedConnection({ x1, y1, x2, y2 }) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.ceil(Math.sqrt(dx * dx + dy * dy));
  const animKey = `conn-${x1}-${y1}-${x2}-${y2}`;

  return (
    <>
      <defs>
        <style>{`
          @keyframes drawLine_${len} {
            from { stroke-dashoffset: ${len}; }
            to   { stroke-dashoffset: 0; }
          }
        `}</style>
      </defs>
      <line
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke="#22c55e"
        strokeWidth={3}
        strokeDasharray={len}
        strokeDashoffset={len}
        style={{ animation: `drawLine_${len} 0.6s ease-out forwards` }}
      />
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function PatchTheNetwork({ step, onComplete, onScoreUpdate, isReplay }) {
  const maxScore = step?.score ?? 30;

  // placements: { slotId -> componentId }
  const [placements, setPlacements] = useState({});
  // which slots just flashed red
  const [flashingSlots, setFlashingSlots] = useState(new Set());
  // score penalty counter
  const [mistakes, setMistakes] = useState(0);
  // active drag id
  const [activeDragId, setActiveDragId] = useState(null);
  // done state
  const [done, setDone] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const placedComponentIds = new Set(Object.values(placements));

  const handleDragStart = useCallback(({ active }) => {
    setActiveDragId(active.id);
  }, []);

  const handleDragEnd = useCallback(({ active, over }) => {
    setActiveDragId(null);
    if (!over) return;

    const componentId = active.id;
    const slotId = over.id;
    const slot = SLOTS.find(s => s.id === slotId);
    if (!slot) return;

    if (slot.accepts === componentId) {
      // Correct placement
      setPlacements(prev => {
        const next = { ...prev, [slotId]: componentId };
        // Check if all slots filled
        if (Object.keys(next).length === SLOTS.length) {
          setTimeout(() => setDone(true), 800);
        }
        return next;
      });
    } else {
      // Wrong placement — flash slot red, return component to toolbox
      setFlashingSlots(prev => new Set([...prev, slotId]));
      setMistakes(m => m + 1);
      setTimeout(() => {
        setFlashingSlots(prev => {
          const next = new Set(prev);
          next.delete(slotId);
          return next;
        });
      }, 600);
    }
  }, []);

  const finalScore = Math.max(10, maxScore - mistakes * 3);

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center gap-6 p-8 text-center"
      >
        <div className="text-4xl font-bold text-green-400">Network Secured</div>
        <p className="text-slate-300 max-w-md">
          Firewall, IDS sensors, and segmentation controls are in place. The network is hardened.
        </p>
        {!isReplay && (
          <div className="flex flex-col items-center gap-1">
            <span className="text-slate-400 text-sm">Score</span>
            <span className="text-3xl font-mono font-bold text-indigo-300">+{finalScore}</span>
            {mistakes === 0 && (
              <span className="text-xs text-green-400 mt-1">Perfect placement — no mistakes</span>
            )}
          </div>
        )}
        <button
          onClick={() => {
            onScoreUpdate && onScoreUpdate(finalScore);
            onComplete && onComplete();
          }}
          className="mt-2 px-8 py-3 bg-green-600 hover:bg-green-500 text-white font-semibold rounded-lg transition-colors"
        >
          Continue
        </button>
      </motion.div>
    );
  }

  const activeComponent = activeDragId
    ? PATCH_COMPONENTS.find(c => c.id === activeDragId)
    : null;

  // Build set of which connection pairs have both endpoints "patched"
  // We animate a green overlay line for each correctly-patched slot that has connectionFrom/To
  const animatedConnections = SLOTS
    .filter(s => placements[s.id] && s.connectionFrom !== undefined)
    .map(s => ({
      x1: NODES[s.connectionFrom].cx,
      y1: NODES[s.connectionFrom].cy,
      x2: NODES[s.connectionTo].cx,
      y2: NODES[s.connectionTo].cy,
      key: s.id,
    }));

  // Dashed indicator lines from slot to nearest node (visual guides)
  const guideLines = SLOTS.map(s => {
    const nearestNode = NODES.reduce((best, n) => {
      const d = Math.sqrt((n.cx - s.cx) ** 2 + (n.cy - s.cy) ** 2);
      const bd = Math.sqrt((best.cx - s.cx) ** 2 + (best.cy - s.cy) ** 2);
      return d < bd ? n : best;
    });
    return { slotId: s.id, x1: s.cx, y1: s.cy, x2: nearestNode.cx, y2: nearestNode.cy };
  });

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col gap-4 p-4 w-full">
        <div className="text-center">
          <h2 className="text-lg font-bold text-white">Patch the Network</h2>
          <p className="text-sm text-slate-400 mt-1">
            Drag each security control to the correct position on the network diagram.
          </p>
        </div>

        <div className="flex gap-4 items-start">
          {/* Toolbox */}
          <div className="flex flex-col gap-3 min-w-[160px]">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
              Toolbox
            </div>
            {PATCH_COMPONENTS.map(c => (
              <DraggableCard
                key={c.id}
                component={c}
                isPlaced={placedComponentIds.has(c.id)}
              />
            ))}
            {placedComponentIds.size === PATCH_COMPONENTS.length && (
              <div className="text-xs text-green-400 text-center mt-2">All placed!</div>
            )}
          </div>

          {/* Network diagram */}
          <div className="flex-1 min-w-0">
            <svg
              viewBox="0 0 760 260"
              width="100%"
              style={{ display: 'block' }}
              aria-label="OT Network Diagram — drag patch components to the marked slots"
            >
              {/* Base connection lines */}
              {CONNECTIONS.map(([fi, ti]) => (
                <line
                  key={`base-${fi}-${ti}`}
                  x1={NODES[fi].cx} y1={NODES[fi].cy}
                  x2={NODES[ti].cx} y2={NODES[ti].cy}
                  stroke="#4b5563"
                  strokeWidth={2}
                />
              ))}

              {/* Guide dashes from slots to nearest node */}
              {guideLines.map(g => (
                <line
                  key={`guide-${g.slotId}`}
                  x1={g.x1} y1={g.y1} x2={g.x2} y2={g.y2}
                  stroke="#374151"
                  strokeWidth={1}
                  strokeDasharray="4 3"
                />
              ))}

              {/* Animated correct-placement lines */}
              {animatedConnections.map(c => (
                <AnimatedConnection key={c.key} x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2} />
              ))}

              {/* Nodes */}
              {NODES.map(node => (
                <g key={node.id}>
                  <circle cx={node.cx} cy={node.cy} r={26} fill={NODE_COLOR} stroke={NODE_STROKE} strokeWidth={2} />
                  {node.label.split('\n').map((line, i, arr) => (
                    <text
                      key={i}
                      x={node.cx}
                      y={node.cy + (i - (arr.length - 1) / 2) * 11}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={9}
                      fontFamily="system-ui, sans-serif"
                      fill="#e5e7eb"
                      fontWeight="600"
                    >
                      {line}
                    </text>
                  ))}
                </g>
              ))}

              {/* Drop slots */}
              {SLOTS.map(slot => (
                <DropSlot
                  key={slot.id}
                  slot={slot}
                  placedComponentId={placements[slot.id] ?? null}
                  isFlashing={flashingSlots.has(slot.id)}
                />
              ))}
            </svg>

            {/* Mistake indicator */}
            {mistakes > 0 && (
              <div className="text-xs text-red-400 text-center mt-1">
                {mistakes} wrong placement{mistakes > 1 ? 's' : ''} — score reduced
              </div>
            )}
          </div>
        </div>
      </div>

      <DragOverlay>
        <CardPreview component={activeComponent} />
      </DragOverlay>
    </DndContext>
  );
}

export default PatchTheNetwork;
